import { FC, memo, useEffect, useMemo, useRef } from 'react';

import { ErrorFallback, Player, PlayerRef } from '@remotion/player';
import * as Sentry from '@sentry/react';

import { Logger } from '@core';

import { useBounds } from './context';
import { FPS, Video } from './VideoCell';
import { VideoError } from './VideoError';
import { VideoGridLoading } from './VideoGridLoading';
import { MemoVideoLayout } from './VideoLayout';
import { VideoScrub } from './VideoScrub';

const VIDEO_PLAYER_MODE = {
    AUTOPLAY: 'autoplay',
    SCRUB: 'scrub',
} as const;

type VideoPlayerModes = (typeof VIDEO_PLAYER_MODE)[keyof typeof VIDEO_PLAYER_MODE];

export type VideoPlayerProps = {
    videos: Video[];
    maxFrame: number;
    mode: VideoPlayerModes;
    loop?: boolean;
    onReplayEnded?: () => void;
    playbackSpeed?: number;
};

const PLAYBACK_RATE_CONTROL = [0.25, 0.5, 1];

const rawPlayerStyle = { position: 'absolute' } as const;

const renderLoading = () => <VideoGridLoading />;
const renderError: ErrorFallback = ({ error }) => {
    Logger.error('<VideoPlayer/> error:' + error.message);
    return <VideoError />;
};

const noop = () => {};

const VideoPlayer: FC<VideoPlayerProps> = ({ videos, maxFrame, mode, loop, onReplayEnded = noop, playbackSpeed }) => {
    const bounds = useBounds();
    const playerRef = useRef<PlayerRef>(null);

    const inputProps = useMemo(() => ({ videos }), [videos]);

    useEffect(() => {
        const current = playerRef?.current;
        if (!current) return;
        current.addEventListener('ended', onReplayEnded);
        return () => current.removeEventListener('ended', onReplayEnded);
    }, [onReplayEnded]);
    useEffect(() => {
        Sentry.captureMessage('VideoPlayer', {
            level: 'info',
            extra: { videos },
        });
    }, [videos]);

    if (!bounds.width || !bounds.height || !maxFrame) {
        return <VideoGridLoading />;
    }

    if (mode === VIDEO_PLAYER_MODE.SCRUB) {
        return (
            <VideoScrub playerRef={playerRef}>
                <Player
                    ref={playerRef}
                    component={MemoVideoLayout}
                    inputProps={inputProps}
                    durationInFrames={maxFrame}
                    fps={FPS.camera}
                    compositionWidth={Math.floor(bounds.width)}
                    compositionHeight={Math.floor(bounds.height)}
                    logLevel="warn"
                    style={rawPlayerStyle}
                    showPlaybackRateControl={PLAYBACK_RATE_CONTROL}
                    showVolumeControls={false}
                    renderLoading={renderLoading}
                    errorFallback={renderError}
                    showPosterWhenBuffering
                    posterFillMode="composition-size"
                    renderPoster={renderLoading}
                    autoPlay={false}
                    loop={loop}
                />
            </VideoScrub>
        );
    }

    if (mode === VIDEO_PLAYER_MODE.AUTOPLAY) {
        return (
            <Player
                ref={playerRef}
                component={MemoVideoLayout}
                inputProps={{ videos }}
                durationInFrames={maxFrame}
                fps={FPS.camera}
                compositionWidth={Math.floor(bounds.width)}
                compositionHeight={Math.floor(bounds.height)}
                logLevel="warn"
                style={rawPlayerStyle}
                showPlaybackRateControl={PLAYBACK_RATE_CONTROL}
                playbackRate={playbackSpeed}
                showVolumeControls={false}
                renderLoading={renderLoading}
                errorFallback={renderError}
                showPosterWhenBuffering
                posterFillMode="composition-size"
                renderPoster={renderLoading}
                autoPlay={VIDEO_PLAYER_MODE.AUTOPLAY === mode}
                acknowledgeRemotionLicense
                loop={loop}
            />
        );
    }

    throw new Error('Unknown Video Mode');
};

export const MemoVideoPlayer = memo(VideoPlayer);
